import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { ChannelFile, Pin, Principal, UUID } from "../sync/types";
import { RichText } from "../text";
import { IconFile } from "./icons";
import { Avatar, Badge, EmptyState, Field, Form, Sheet } from "./ui";
import { locale, t } from "../i18n";
import { RecipientPicker, type To } from "./RecipientPicker";
import type { Session } from "../session";

// clearHistory clears a DM or group DM's history for oneself once one
// confirms it (ADR-0024); it reports whether it did.
export async function clearHistory(session: Session, id: UUID): Promise<boolean> {
  const name = session.channelName(id);
  if (!window.confirm(t("Clear your history with {name}? The messages so far disappear for you, on all your devices. The others in the conversation keep theirs, and you can't get them back.", { name }))) return false;
  await session.clearHistory(id);
  return true;
}

export type DetailsTab = "about" | "members" | "pinned" | "files";

type Level = "all" | "mentions" | "none";
const levels = (): [Level, string][] => [
  ["all", t("Every message")],
  ["mentions", t("Mentions")],
  ["none", t("Muted")],
];
const levelHints = (): Record<Level, string> => ({
  all: t("A pop-up and a sound for every new message, and the channel turns bold."),
  mentions: t("A pop-up and a sound only when someone mentions you; the channel turns bold for mentions."),
  none: t("Nothing at all, mentions included, though mentions still show a count. The channel stays dim."),
});

type Member = { principal_id: UUID; role: string; joined_at: string };

// ChannelDetails shows a channel's name, topic and members, and lets people
// change what their role allows: any member sets the topic, adds people and
// leaves; the owner or an admin renames, archives and removes people.
export function ChannelDetails({ id, tab: initial, onClose }: { id: UUID; tab: DetailsTab; onClose: () => void }) {
  const session = useSession();
  const state = useLantern();
  const cs = state.channels.get(id);
  const [tab, setTab] = useState<DetailsTab>(initial);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState("");

  const load = () =>
    session.api.memberList(id).then(
      (r) => setMembers(r.members),
      (err) => setError(String(err)),
    );
  useEffect(() => void load(), [id]);

  if (!cs) return null;
  const ch = cs.channel;
  const me = state.me!;
  const dm = ch.kind === "dm" || ch.kind === "group_dm";
  const manager = ch.membership?.role === "owner" || me.role === "admin";
  const archived = !!ch.archived_at;
  const name = session.channelName(id);

  async function act(f: () => Promise<unknown>) {
    setError("");
    try {
      await f();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  // The channel's own event brings every member's view up to date; this
  // shows the change at once.
  const patch = (c: { name?: string; topic?: string; description?: string; archived_at?: string | null; kind?: "private" }) =>
    session.store.update((s) => {
      const x = s.channels.get(id);
      if (x) Object.assign(x.channel, c);
    });

  async function leave() {
    if (!window.confirm(ch.kind === "private" ? t("Leave {name}? You will need to be added again to come back.", { name }) : t("Leave {name}?", { name }))) return;
    await act(async () => {
      await session.api.removeMember(id, me.id);
      session.store.update((s) => s.channels.delete(id));
      onClose();
      navigate("/");
    });
  }

  return (
    <Sheet title={name} onClose={onClose} className="page details-sheet">
      {!dm && (
        <div className="segmented" role="tablist">
          <button role="tab" aria-selected={tab === "about"} className={tab === "about" ? "active" : ""} onClick={() => setTab("about")}>
            {t("About")}
          </button>
          <button role="tab" aria-selected={tab === "members"} className={tab === "members" ? "active" : ""} onClick={() => setTab("members")}>
            {t("Members")}
            {members ? ` · ${members.length}` : ""}
          </button>
          <button role="tab" aria-selected={tab === "pinned"} className={tab === "pinned" ? "active" : ""} onClick={() => setTab("pinned")}>
            {t("Pinned")}
            {state.pins.get(id)?.size ? ` · ${state.pins.get(id)!.size}` : ""}
          </button>
          <button role="tab" aria-selected={tab === "files"} className={tab === "files" ? "active" : ""} onClick={() => setTab("files")}>
            {t("Files")}
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {archived && <p className="note">{t("This channel is archived: people can read it, but nothing more can be posted.")}</p>}

      {tab === "about" || dm ? (
        <div className="details-about">
          {!dm && (
            <EditableRow
              label={t("Channel name")}
              value={ch.name ?? ""}
              shown={`#${ch.name}`}
              canEdit={manager && !archived}
              hint={t("Lowercase letters, digits, - and _.")}
              transform={(v) => v.toLowerCase().replace(/^#/, "")}
              onSave={(v) =>
                act(async () => {
                  const { channel } = await session.api.updateChannel(id, { name: v });
                  patch({ name: channel.name });
                })
              }
            />
          )}
          <EditableRow
            label={t("Topic")}
            value={ch.topic}
            shown={ch.topic || <span className="muted">{t("No topic")}</span>}
            canEdit={!archived}
            hint={t("One line that tells people what this is for.")}
            onSave={(v) =>
              act(async () => {
                const { channel } = await session.api.updateChannel(id, { topic: v });
                patch({ topic: channel.topic });
              })
            }
          />
          {!dm && (
            <EditableRow
              label={t("Description")}
              value={ch.description ?? ""}
              shown={ch.description ? <span className="description">{ch.description}</span> : <span className="muted">{t("No description")}</span>}
              canEdit={!archived && state.me?.role !== "guest"}
              hint={t("What the channel is for, for those deciding whether to join.")}
              multiline
              onSave={(v) =>
                act(async () => {
                  const { channel } = await session.api.updateChannel(id, { description: v });
                  patch({ description: channel.description ?? "" });
                })
              }
            />
          )}
          {ch.membership && (
            <div className="detail-row">
              <span className="detail-label">{t("Notifications")}</span>
              <div className="segmented notify-levels" role="radiogroup" aria-label={t("Notifications")}>
                {(dm ? levels().filter(([l]) => l !== "mentions") : levels()).map(([level, label]) => (
                  <button
                    key={level}
                    role="radio"
                    aria-checked={ch.membership!.notify_level === level}
                    className={ch.membership!.notify_level === level ? "active" : ""}
                    onClick={() =>
                      act(async () => {
                        await session.api.setNotifyLevel(id, level);
                        session.store.update((s) => {
                          const m = s.channels.get(id)?.channel.membership;
                          if (m) m.notify_level = level;
                        });
                      })
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className="field-hint">{levelHints()[ch.membership.notify_level]}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">{t("Created")}</span>
            <span>{t("by {name} on {date}", { name: session.name(ch.created_by), date: new Date(ch.created_at).toLocaleDateString(locale, { dateStyle: "long" }) })}</span>
          </div>
          {dm && members && (
            <div className="detail-row">
              <span className="detail-label">{t("People")}</span>
              <span>{members.map((m) => session.name(m.principal_id)).join(", ")}</span>
            </div>
          )}
          {dm && members && <AddToConversation members={members.map((m) => m.principal_id)} onDone={onClose} />}
          <div className="details-actions">
            {dm && (
              <button className="danger" onClick={() => void act(async () => void ((await clearHistory(session, id)) && onClose()))}>
                {t("Clear history…")}
              </button>
            )}
            {ch.kind !== "dm" && (
              <button className="danger" onClick={() => void leave()}>
                {dm ? t("Leave conversation") : t("Leave channel")}
              </button>
            )}
            {ch.kind === "public" && ch.membership && manager && (
              <button
                onClick={() =>
                  window.confirm(t("Make {name} private? From now on only its members can find it, read it and its history, and see its files. It cannot be made public again.", { name })) &&
                  void act(async () => {
                    await session.api.makePrivate(id);
                    patch({ kind: "private" });
                  })
                }
              >
                {t("Make private")}
              </button>
            )}
            {!dm && manager && (
              <button
                onClick={() =>
                  (archived || window.confirm(t("Archive {name}? Nobody can post in it until it is unarchived; people can still read it.", { name }))) &&
                  void act(async () => {
                    const { channel } = await session.api.setArchived(id, !archived);
                    patch({ archived_at: channel.archived_at ?? null });
                  })
                }
              >
                {archived ? t("Unarchive channel") : t("Archive channel")}
              </button>
            )}
          </div>
        </div>
      ) : tab === "members" ? (
        <MembersTab id={id} members={members} canRemove={manager} canAdd={!archived} onChanged={load} onError={setError} />
      ) : tab === "pinned" ? (
        <PinnedTab id={id} onClose={onClose} onError={setError} />
      ) : (
        <FilesTab id={id} onClose={onClose} />
      )}
    </Sheet>
  );
}

// EditableRow shows a value with an Edit button that turns it into a field.
function EditableRow({
  label,
  value,
  shown,
  canEdit,
  hint,
  transform = (v) => v,
  multiline = false,
  onSave,
}: {
  label: string;
  value: string;
  shown: ReactNode;
  canEdit: boolean;
  hint?: string;
  transform?: (v: string) => string;
  multiline?: boolean;
  onSave: (v: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  async function save(e: FormEvent) {
    e.preventDefault();
    await onSave(draft.trim());
    setEditing(false);
  }
  if (editing)
    return (
      <Form className="detail-row editing" onSubmit={save}>
        <Field label={label} hint={hint}>
          {multiline ? (
            <textarea value={draft} onChange={(e) => setDraft(transform(e.target.value))} rows={4} maxLength={1000} autoFocus />
          ) : (
            <input value={draft} onChange={(e) => setDraft(transform(e.target.value))} autoFocus />
          )}
        </Field>
        <div className="form-actions">
          <button type="button" onClick={() => setEditing(false)}>
            {t("Cancel")}
          </button>
          <button type="submit">{t("Save")}</button>
        </div>
      </Form>
    );
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className="detail-value">{shown}</span>
      {canEdit && (
        <button
          className="link"
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
          aria-label={t("Edit {label}", { label: label.toLowerCase() })}
        >
          {t("Edit")}
        </button>
      )}
    </div>
  );
}

function MembersTab({
  id,
  members,
  canRemove,
  canAdd,
  onChanged,
  onError,
}: {
  id: UUID;
  members: Member[] | null;
  canRemove: boolean;
  canAdd: boolean;
  onChanged: () => void;
  onError: (e: string) => void;
}) {
  const session = useSession();
  const state = useLantern();
  const [q, setQ] = useState("");
  const inChannel = new Set(members?.map((m) => m.principal_id));
  const query = q.trim().toLowerCase().replace(/^@/, "");
  const matches = (p: Principal) => !query || p.handle.toLowerCase().includes(query) || p.display_name.toLowerCase().includes(query);
  const candidates = query
    ? [...session.directory.values()].filter((p) => !inChannel.has(p.id) && p.status === "active" && p.kind !== "system" && matches(p)).slice(0, 6)
    : [];

  async function run(f: () => Promise<unknown>) {
    onError("");
    try {
      await f();
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  }

  const shown = (members ?? []).filter((m) => {
    const p = session.directory.get(m.principal_id);
    return !p || matches(p);
  });
  return (
    <div className="details-members">
      <input
        className="member-filter"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={canAdd ? t("Find or add people") : t("Find people")}
        aria-label={t("Find or add people")}
        autoFocus
      />
      {canAdd && candidates.length > 0 && (
        <ul className="rows member-candidates" aria-label={t("Add to channel")}>
          {candidates.map((p) => (
            <li key={p.id}>
              <Avatar id={p.id} name={p.display_name || p.handle} size={28} />
              <span className="row-main">
                <span className="row-title">{p.display_name || p.handle}</span>
                <span className="row-meta">
                  @{p.handle}
                  {p.kind === "agent" && ` · ${t("agent")}`}
                </span>
              </span>
              <button className="small" onClick={() => void run(() => session.api.addMember(id, p.id).then(() => setQ("")))}>
                {t("Add")}
              </button>
            </li>
          ))}
        </ul>
      )}
      <ul className="rows member-list" aria-label={t("Members")}>
        {shown.map((m) => {
          const p = session.directory.get(m.principal_id);
          const self = m.principal_id === state.me?.id;
          return (
            <li key={m.principal_id}>
              <Avatar id={m.principal_id} name={session.name(m.principal_id)} size={28} presence={state.presence.get(m.principal_id)} />
              <span className="row-main">
                <span className="row-title profile-link" data-profile={m.principal_id} role="button" tabIndex={0}>
                  {session.name(m.principal_id)}
                  {self && <span className="muted"> {t("(you)")}</span>}
                </span>
                <span className="row-meta">{p ? `@${p.handle}` : ""}</span>
              </span>
              {m.role === "owner" && <Badge tone="accent">{t("Owner")}</Badge>}
              {p?.kind === "agent" && <Badge>{t("Agent")}</Badge>}
              {canRemove && !self && (
                <button
                  className="small ghost"
                  onClick={() =>
                    window.confirm(t("Remove {name} from {channel}?", { name: session.name(m.principal_id), channel: session.channelName(id) })) &&
                    void run(() => session.api.removeMember(id, m.principal_id))
                  }
                  aria-label={t("Remove {name}", { name: session.name(m.principal_id) })}
                >
                  {t("Remove")}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PinnedTab({ id, onClose, onError }: { id: UUID; onClose: () => void; onError: (e: string) => void }) {
  const session = useSession();
  const state = useLantern();
  const [pins, setPins] = useState<Pin[] | null>(null);
  const count = state.pins.get(id)?.size ?? 0;
  useEffect(() => {
    session.loadPins(id).then(setPins, (err) => onError(String(err)));
  }, [id, count]);
  if (!pins) return <p className="muted">{t("Loading…")}</p>;
  if (pins.length === 0)
    return (
      <EmptyState icon="📌" title={t("Nothing pinned")}>
        {t("Pin important messages from their More actions menu, and everyone here finds them in this tab.")}
      </EmptyState>
    );
  return (
    <ul className="rows pinned-list">
      {pins.map((p) => (
        <li key={p.message.id}>
          <div className="pinned-meta">
            <span className="muted">
              {t("Pinned by {name} · {date}", { name: session.name(p.pinned_by), date: new Date(p.pinned_at).toLocaleDateString(locale, { month: "short", day: "numeric" }) })}
            </span>
            <button
              className="small ghost"
              onClick={() => {
                onClose();
                navigate(p.message.thread_root_id ? `/c/${id}/t/${p.message.thread_root_id}` : `/c/${id}/m/${p.message.id}`);
              }}
            >
              {t("Show")}
            </button>
            <button className="small ghost" onClick={() => void session.setPinned(id, p.message.id, false).catch((err) => onError(String(err)))}>
              {t("Unpin")}
            </button>
          </div>
          <div className="pinned-text">
            <strong>{session.name(p.message.author.id)}</strong>{" "}
            <RichText text={p.message.text} name={(x) => session.name(x)} me={state.me?.id} inline />
          </div>
        </li>
      ))}
    </ul>
  );
}

function FilesTab({ id, onClose }: { id: UUID; onClose: () => void }) {
  const session = useSession();
  const [files, setFiles] = useState<ChannelFile[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    session.api.channelFiles(id).then(
      (r) => setFiles(r.files),
      (err) => setError(String(err)),
    );
  }, [id]);
  async function download(f: ChannelFile) {
    try {
      window.open(await session.fileURL(f.file.id), "_blank", "noopener");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  if (error) return <p className="error">{error}</p>;
  if (!files) return <p className="muted">{t("Loading…")}</p>;
  if (files.length === 0)
    return (
      <EmptyState icon="📁" title={t("No files yet")}>
        {t("Files posted here, in messages or threads, are listed in this tab.")}
      </EmptyState>
    );
  return (
    <ul className="rows files-list">
      {files.map((f) => (
        <li key={f.file.id + f.message_id}>
          <span className="file-icon" aria-hidden>
            {f.file.mime.startsWith("image/") ? "🖼️" : <IconFile />}
          </span>
          <span className="row-main">
            <button className="row-title link" onClick={() => void download(f)}>
              {f.file.name}
            </button>
            <span className="row-meta">
              {size(f.file.size)} · {session.name(f.author_id)} · {new Date(f.posted_at).toLocaleDateString(locale, { month: "short", day: "numeric" })}
            </span>
          </span>
          <button
            className="small ghost"
            onClick={() => {
              onClose();
              navigate(f.thread_root_id ? `/c/${id}/t/${f.thread_root_id}` : `/c/${id}/m/${f.message_id}`);
            }}
          >
            {t("Show message")}
          </button>
        </li>
      ))}
    </ul>
  );
}

function size(n: number) {
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// AddToConversation brings more people into a DM, as Slack does: a new
// group conversation with everyone, since a DM's members do not change and
// its history stays between those in it.
function AddToConversation({ members, onDone }: { members: UUID[]; onDone: () => void }) {
  const session = useSession();
  const [to, setTo] = useState<To[]>([]);
  const [error, setError] = useState("");
  const people = to.filter((x) => x.kind === "person");
  async function start() {
    setError("");
    try {
      const me = session.store.getSnapshot().me?.id;
      const ids = [...new Set([...members, ...people.map((p) => p.id)])].filter((id) => id !== me);
      const { channel } = await session.api.openDM(ids);
      await session.loadDMMembers();
      onDone();
      navigate(`/c/${channel.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <div className="detail-row add-to-dm">
      <span className="detail-label">{t("Add people")}</span>
      <div>
        <RecipientPicker to={people} onChange={setTo} />
        <p className="field-hint">{t("Starts a new conversation with everyone; what was said here stays here.")}</p>
        {error && <p className="error">{error}</p>}
        <button className="small" disabled={people.length === 0} onClick={() => void start()}>
          {t("Start the conversation")}
        </button>
      </div>
    </div>
  );
}
